import { NavLink, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown, ExternalLink } from "lucide-react";

// Primary navigation per the rebuild brief.
// Order: Meet the Kids | Awareness | Get Involved | Statistics | Contact | FOSTER CARE ANTHEM
const MAIN_LINKS = [
  {
    to: "/meet-the-kids", label: "Meet the Kids",
    dropdown: [
      { to: "/meet-the-kids/malik", label: "Meet Malik" },
      { to: "/meet-the-kids/ariana", label: "Meet Ariana" },
      { to: "/meet-the-kids/jayden", label: "Meet Jayden" },
      { to: "/meet-the-kids/sophia", label: "Meet Sophia" },
    ],
  },
  { to: "/everyday-awareness", label: "Everyday Awareness" },
  { to: "/get-involved", label: "Get Involved" },
  { to: "/statistics", label: "Statistics" },
  { to: "/contact", label: "Contact" },
  {
    to: "/anthem", label: "Foster Care Anthem", highlight: true,
    dropdown: [
      { to: "/anthem/mission", label: "The Mission" },
      { to: "/anthem/catalog", label: "The Catalog" },
      { to: "/anthem/stories", label: "Stories" },
      { to: "/anthem/licensing", label: "Licensing" },
      { to: "https://fostercareanthemproject.com/", label: "Visit Full Site", external: true },
    ],
  },
];

const ANTHEM_LINKS = [
  { to: "/anthem/mission", label: "The Mission" },
  { to: "/anthem/catalog", label: "The Catalog" },
  { to: "/anthem/stories", label: "Stories" },
  { to: "/anthem/licensing", label: "Licensing" },
];

export default function Navbar({ variant = "fc" }) {
  const [open, setOpen] = useState(false);
  const [activeDrop, setActiveDrop] = useState(null);
  const location = useLocation();
  const isAnthem = variant === "anthem";
  const navRef = useRef(null);

  useEffect(() => { setOpen(false); setActiveDrop(null); }, [location.pathname]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setActiveDrop(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const navClass = isAnthem
    ? "border-b border-an-border bg-an-bg text-an-ink"
    : "border-b border-fc-border bg-fc-bg text-fc-ink";

  const subClass = isAnthem
    ? "border-b border-an-border bg-an-surface/60 text-an-ink"
    : "border-b border-fc-border bg-fc-surface/50 text-fc-ink";

  return (
    <header className="sticky top-0 z-40" data-testid="site-header">
      <nav className={`${navClass} relative z-40`} ref={navRef}>
        <div className="container-xl flex items-center justify-between py-5 gap-6">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-link">
            <div className={`w-9 h-9 border ${isAnthem ? "border-an-ink" : "border-fc-ink"} flex items-center justify-center font-display text-base leading-none`}>
              365
            </div>
            <div className="font-display text-xl md:text-2xl leading-none">
              FosterCareAwareness<span className={isAnthem ? "text-an-gold" : "text-fc-accent"}>365</span>
            </div>
          </Link>

          <div className="hidden xl:flex items-center gap-6 ml-auto">
            {MAIN_LINKS.map((l) => {
              if (l.dropdown) {
                const isActive = location.pathname.startsWith(l.to);
                const dropOpen = activeDrop === l.to;
                return (
                  <div
                    key={l.to}
                    className="relative flex items-center"
                    onMouseEnter={() => setActiveDrop(l.to)}
                    onMouseLeave={() => setActiveDrop(null)}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`overline link-ul whitespace-nowrap ${l.highlight ? (isAnthem ? "text-an-gold" : "text-fc-accent") : ""} ${isActive ? "opacity-100" : "opacity-85"} hover:opacity-100`}
                    >
                      {l.label}
                    </NavLink>
                    <button
                      type="button"
                      onClick={() => setActiveDrop(dropOpen ? null : l.to)}
                      aria-label={`Toggle ${l.label} menu`}
                      aria-expanded={dropOpen}
                      data-testid={`${l.label.toLowerCase().replace(/\s+/g, '-')}-dropdown-toggle`}
                      className="ml-1 p-1 -mr-1 hover:opacity-100 opacity-70"
                    >
                      <ChevronDown size={11} className={`transition-transform ${dropOpen ? "rotate-180" : ""}`} />
                    </button>
                    {dropOpen && (
                      <div className="absolute right-0 top-full pt-2 w-56 z-50" data-testid={`${l.label.toLowerCase().replace(/\s+/g, '-')}-dropdown`}>
                        <div className={`border ${isAnthem ? "border-an-border bg-an-bg" : "border-fc-border bg-fc-bg"} shadow-lg`}>
                          {l.dropdown.map((d) => (
                            d.external ? (
                              <a
                                key={d.to}
                                href={d.to}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center justify-between px-5 py-3 overline transition-colors border-b last:border-b-0 ${isAnthem ? "border-an-border hover:bg-an-surface text-an-gold" : "border-fc-border hover:bg-fc-surface text-fc-accent"}`}
                                data-testid={`dropdown-${d.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <span>{d.label}</span>
                                <ExternalLink size={11} />
                              </a>
                            ) : (
                              <Link
                                key={d.to}
                                to={d.to}
                                className={`block px-5 py-3 overline transition-colors border-b last:border-b-0 ${isAnthem ? "border-an-border hover:bg-an-surface" : "border-fc-border hover:bg-fc-surface"}`}
                                data-testid={`dropdown-${d.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                {d.label}
                              </Link>
                            )
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `overline link-ul whitespace-nowrap ${
                      l.highlight ? (isAnthem ? "text-an-gold" : "text-fc-accent") : ""
                    } ${isActive ? "opacity-100" : "opacity-85"} hover:opacity-100`
                  }
                >
                  {l.label}
                </NavLink>
              );
            })}
          </div>

          <button
            className="xl:hidden p-2"
            onClick={() => setOpen(!open)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {isAnthem && (
        <div className={`${subClass} hidden lg:block`} data-testid="anthem-subnav">
          <div className="container-xl flex items-center gap-8 py-3">
            <span className="overline" style={{opacity:0.55}}>Foster Care Anthem</span>
            {ANTHEM_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`anthem-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `overline link-ul ${isActive ? "text-an-gold" : ""}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {open && (
        <div className={`${isAnthem ? "bg-an-bg text-an-ink" : "bg-fc-bg text-fc-ink"} xl:hidden border-t ${isAnthem ? "border-an-border" : "border-fc-border"} max-h-[80vh] overflow-y-auto`} data-testid="mobile-menu">
          <div className="container-xl py-6 flex flex-col gap-4">
            {MAIN_LINKS.map((l) => (
              <div key={l.to}>
                <NavLink to={l.to} end={l.to === "/"} className={`font-display text-2xl block ${l.highlight ? (isAnthem ? "text-an-gold" : "text-fc-accent") : ""}`} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  {l.label}
                </NavLink>
                {l.dropdown && (
                  <div className="mt-2 pl-4 space-y-2">
                    {l.dropdown.map((d) => (
                      d.external ? (
                        <a key={d.to} href={d.to} target="_blank" rel="noopener noreferrer" className={`overline block opacity-70 inline-flex items-center gap-1.5 ${isAnthem ? "text-an-gold" : "text-fc-accent"}`}>
                          {d.label} <ExternalLink size={11} />
                        </a>
                      ) : (
                        <NavLink key={d.to} to={d.to} className="overline block opacity-70">{d.label}</NavLink>
                      )
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
